/**
 * Kubernetes 状态管理工具
 */

// K8S 状态枚举
export type K8SStatus =
  | 'STOP'
  | 'ERROR'
  | 'RUNNING'
  | 'ABORTED'
  | 'DEPLOYING'
  | 'UPDATING'
  | 'STOPPING'
  | 'ABORTING'
  | 'DELETED'
  | 'UPDATE_ABORTED'

export type ImageBuildStatus =
  | 'Preparing'
  | 'Building'
  | 'Stopped'
  | 'Fail'
  | 'Success'

/**
 * 获取 K8S 状态对应的样式类名
 * @param status 状态
 * @returns Tailwind CSS 类名
 */
export function getK8SStatusClass(status?: string): string {
  if (!status) return 'bg-gray-100 text-gray-800'

  const statusMap: Record<K8SStatus, string> = {
    STOP: 'bg-gray-100 text-gray-800',
    ERROR: 'bg-red-100 text-red-800',
    RUNNING: 'bg-green-100 text-green-800',
    ABORTED: 'bg-red-100 text-red-800',
    UPDATE_ABORTED: 'bg-red-100 text-red-800',
    DEPLOYING: 'bg-blue-100 text-blue-800',
    UPDATING: 'bg-yellow-100 text-yellow-800',
    STOPPING: 'bg-gray-100 text-gray-800',
    ABORTING: 'bg-red-100 text-red-800',
    DELETED: 'bg-gray-100 text-gray-800'
  }

  return statusMap[status as K8SStatus] || 'bg-gray-100 text-gray-800'
}

/**
 * 获取 K8S 状态对应的指示点样式类名
 * @param status 状态
 * @returns Tailwind CSS 类名
 */
export function getK8SDotsStatusClass(status?: string): string {
  if (!status) return 'bg-gray-500'

  const statusMap: Record<K8SStatus, string> = {
    STOP: 'bg-gray-500',
    ERROR: 'bg-red-500',
    RUNNING: 'bg-green-500',
    ABORTED: 'bg-red-500',
    UPDATE_ABORTED: 'bg-red-500',
    DEPLOYING: 'bg-blue-500 animate-pulse',
    UPDATING: 'bg-yellow-500 animate-pulse',
    STOPPING: 'bg-gray-500 animate-pulse',
    ABORTING: 'bg-red-500 animate-pulse',
    DELETED: 'bg-gray-500'
  }

  return statusMap[status as K8SStatus] || 'bg-gray-500'
}

/**
 * 获取 K8S 状态文本映射表
 * @returns 状态文本映射对象
 */
export function getK8SStatusTextMap(): Record<K8SStatus, string> {
  return {
    STOP: '停止',
    ERROR: '异常',
    RUNNING: '运行中',
    ABORTED: '已中断',
    DEPLOYING: '启动中',
    UPDATING: '升级中',
    STOPPING: '停止中',
    ABORTING: '中断中',
    UPDATE_ABORTED: '已中断',
    DELETED: '已删除'
  }
}

/**
 * 获取 K8S 状态的中文文本
 * @param status 状态
 * @returns 中文状态文本
 */
export function getK8SStatusText(status?: string): string {
  if (!status) return '未知'

  const statusMap = getK8SStatusTextMap()
  return statusMap[status as K8SStatus] || '未知'
}

/**
 * 获取镜像构建状态的中文文本
 * @param status 构建状态
 * @returns 中文状态文本
 */
export function getK8SImageStatusText(status?: string): string {
  if (!status) return '未知'

  const statusMap: Record<ImageBuildStatus, string> = {
    Preparing: '正在准备',
    Building: '正在构建',
    Stopped: '已被中止',
    Fail: '构建失败',
    Success: '构建成功'
  }

  return statusMap[status as ImageBuildStatus] || '尚未构建'
}

/**
 * 判断状态是否为进行中状态
 * @param status 状态
 * @returns 是否为进行中状态
 */
export function isProgressStatus(status?: string): boolean {
  if (!status) return false

  const progressStatuses: K8SStatus[] = [
    'DEPLOYING',
    'UPDATING',
    'STOPPING',
    'ABORTING'
  ]

  return progressStatuses.includes(status as K8SStatus)
}

/**
 * 判断状态是否为错误状态
 * @param status 状态
 * @returns 是否为错误状态
 */
export function isErrorStatus(status?: string): boolean {
  if (!status) return false

  const errorStatuses: K8SStatus[] = ['ERROR', 'ABORTED']
  return errorStatuses.includes(status as K8SStatus)
}

/**
 * 判断状态是否为正常运行状态
 * @param status 状态
 * @returns 是否为正常运行状态
 */
export function isRunningStatus(status?: string): boolean {
  return status === 'RUNNING'
}
